import { useEffect, useState, type ReactNode } from "react";
import {
  DENEME_HAVUZ_TURLERI,
  SAAT_DILIMI,
  denemeBolumSemasi,
  denemeHavuzSuzgeciSemasi,
  pencereSuresiMetni,
  yerelAn,
  yerelParcalar,
} from "@koclukpanel/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  DurumRozeti,
  FiltreKarti,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Sekmeler,
  Tablo,
  cn,
} from "@koclukpanel/ui";
import { useSudoKapisi } from "./SudoKapisi";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  api,
  anahtarKaydet,
  cevapAnahtari,
  denemeAnalizi,
  denemeGuncelle,
  type DenemeBolumu,
} from "./api";
import { SonucGirisi } from "./SonucGirisi";
import { DenemeKatilim } from "./DenemeKatilim";
import { DenemePencereDuzenle } from "./DenemePencereDuzenle";
import {
  havuzKaydet,
  havuzYayinevleri,
  havuzYayineviEkle,
  havuzDenemeleriSayfali as denemeleriSayfali,
  type HavuzDenemeSatiri as DenemeSatiri,
} from "./deneme-havuz-api";
import { baglantiHatasi } from "./gorev-icerigi";

/**
 * DENEME TANIMLAMA VE CEVAP ANAHTARI — F-EXM-01/02/03
 *
 * ================================================================
 * BU EKRAN YOKKEN SUNUCU TARAFI DA YARIM KALIYORDU
 * ================================================================
 * `POST /denemeler` vardı, `POST /denemeler/bolumler/:id/anahtar`
 * vardı — ama personelin tanımlanmış denemeleri göreceği bir LİSTE
 * ucu hiç yazılmamıştı. Öğrenci tarafında `bana-atananlar` var;
 * personelde karşılığı yoktu.
 *
 * Sonuç: bir deneme açıldıktan sonra onu BULMANIN yolu yoktu, yani
 * cevap anahtarı da girilemiyordu — anahtar ucu bölüm kimliği istiyor
 * ve o kimlik yalnızca listede görünüyor. `GET /denemeler` bu ekranla
 * birlikte yazıldı.
 *
 * ================================================================
 * ANAHTAR EKSİKSE EKRAN BUNU SÖYLÜYOR
 * ================================================================
 * Eksik anahtar sessiz bir arıza: deneme tanımlı görünür, sonuç
 * girilir, net hesabı yanlış çıkar ve kimse sebebini aramaz. Her
 * bölümün yanında "anahtar var / eksik" rozeti duruyor.
 */

const TURLER = DENEME_HAVUZ_TURLERI;

const TUR_ADI: Record<string, string> = {
  TYT: "TYT",
  AYT: "AYT",
  YDT: "YDT (Dil)",
  LGS: "LGS",
  BRANCH: "Branş denemesi",
  KPSS: "KPSS",
  DGS: "DGS",
  OTHER: "Diğer",
};

const KAYNAK_ADI: Record<string, string> = {
  PUBLISHER: "Yayınevi",
  IN_HOUSE: "Kurum içi",
};

/** `YYYY-AA-GG` — `<input type="date">` bu biçimi bekliyor. */
/*
  Yerel (Istanbul) bugun. `toISOString()` UTC gunu veriyordu: gece
  00:00-03:00 arasi acilan formda varsayilan tarih DUN oluyordu.
*/
function bugunISO(): string {
  return yerelParcalar(new Date()).gun;
}

/** "7 Ekim 09:30" — saat dilimi ACIK; tarayicinin dilimine birakilmiyor. */
function yerelAnMetni(t: string): string {
  return new Date(t).toLocaleString("tr-TR", {
    timeZone: SAAT_DILIMI,
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Cevap giris penceresi + suresi — F-EXM-01a. Panel listesi ve form
 * onizlemesi AYNI metni kullaniyor.
 */
export function pencereMetni(baslangic: string, bitis: string): string {
  const sure = pencereSuresiMetni(baslangic, bitis);
  return `${yerelAnMetni(baslangic)} – ${yerelAnMetni(bitis)}${sure ? ` (${sure})` : ""}`;
}

/**
 * IKI SEKME, IKI AYRI IZIN.
 *
 * "Denemeler" kurumun deneme tanimlarini (`deneme.sinav.yonet`),
 * "Sonuc girisi" uzmanin sonuc yazmasini (`deneme.sonuc.gir`)
 * kapsiyor. Menu ikisinden BIRINE bagli; sekmeler de ayri ayri
 * suzuluyor — tasimadigi sekmeyi gostermek, tiklayinca 403 alinacak
 * bir cikmaz sokak acardi.
 *
 * Tek sekme kaliyorsa sekme cubugu HIC cizilmiyor.
 */
export function DenemeTanim({
  yonetebilir = false,
  duzenleyebilir = false,
  sonucGirebilir = false,
  atayabilir = false,
}: {
  yonetebilir?: boolean;
  /** F-EXM-14 — oluşturma, mevcut ortak tanımı değiştirme hakkı vermez. */
  duzenleyebilir?: boolean;
  sonucGirebilir?: boolean;
  /** F-EXM-04a — `deneme.atama.yonet`; düğme ipucu, karar sunucuda. */
  atayabilir?: boolean;
}) {
  const [gorunum, setGorunum] = useState<"liste" | "sonuc">(
    yonetebilir || atayabilir ? "liste" : "sonuc",
  );

  const sekmeler = [
    ...(yonetebilir || atayabilir ? ([["liste", "Denemeler"]] as const) : []),
    ...(sonucGirebilir ? ([["sonuc", "Sonuç girişi"]] as const) : []),
  ];

  if (sekmeler.length === 0) {
    return (
      <BosDurum
        baslik="Deneme yetkiniz yok"
        aciklama="Bu ekran deneme tanımlama ve sonuç girişi içindir."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        tutulmayan bir söz veriyor.

        Şerit ZATEN yetkiye bağlı çiziliyor: tek sekme kalırsa hiç
        çizilmiyor, çünkü tek seçenekli bir "seçim" seçim değildir.
      */}
      {sekmeler.length > 1 && (
        <Sekmeler
          etiket="Deneme görünümü"
          secili={gorunum}
          onSec={setGorunum}
          sekmeler={sekmeler.map(([deger, etiket]) => ({ deger, etiket }))}
        />
      )}

      {gorunum === "sonuc" && sonucGirebilir ? (
        <SonucGirisi />
      ) : (
        <DenemeListesiTanim
          yonetebilir={yonetebilir}
          duzenleyebilir={duzenleyebilir}
          atayabilir={atayabilir}
        />
      )}
    </div>
  );
}

/** Sayfa başına deneme — sunucu en çok 100'e izin verir; 20 mobil/masaüstü için okunur. */
const SAYFA_BOYUTU = 20;

/**
 * Deneme tablosu — "Deneme tanımla" sekmesi ve (sürüm 9a L2) "Deneme Sınavları"
 * girişi aynı bileşeni kullanır. `filtreEki` filtre kartına ek alan koyar (girişte
 * öğrenci seçici); verildiğinde boş liste de kart düzeninde çizilir, ki filtre
 * kartı kaybolmasın.
 */
export function DenemeListesiTanim({
  yonetebilir = false,
  duzenleyebilir = false,
  atayabilir = false,
  filtreEki,
}: {
  yonetebilir?: boolean;
  /** F-EXM-14 — oluşturma, mevcut ortak tanımı değiştirme hakkı vermez. */
  duzenleyebilir?: boolean;
  atayabilir?: boolean;
  filtreEki?: ReactNode;
}) {
  const [yeniAcik, setYeniAcik] = useState(false);
  const [acik, setAcik] = useState<string | null>(null);
  const [araYazi, setAraYazi] = useState("");
  const [ara, setAra] = useState("");
  const [dilim, setDilim] = useState<DenemeDilimi>("tumu");
  const [sayfa, setSayfa] = useState(1);
  // F-EXM-15 — süzgeçler sorgu anahtarına da girer; başka süzgecin önbelleği gösterilmez.
  const [yil, setYil] = useState("");
  const [donem, setDonem] = useState("");
  const [yayinevi, setYayinevi] = useState("");
  const [seri, setSeri] = useState("");
  const yayinevleri = useQuery({ queryKey: ["deneme-yayinevleri"], queryFn: havuzYayinevleri });
  const [suzgec, setSuzgec] = useState<{
    academicYear?: string;
    term?: 1 | 2;
    publisherId?: string;
    seriesName?: string;
  }>({});
  const suzgecCozumu = denemeHavuzSuzgeciSemasi.safeParse({
    academicYear: yil || undefined,
    term: donem || undefined,
    publisherId: yayinevi || undefined,
    seriesName: seri.trim() || undefined,
  });
  const suzgecVar = Object.values(suzgec).some(Boolean);
  const suzgecAlani = (degistir: (g: string) => void) => (deger: string) => {
    degistir(deger);
    setSayfa(1);
  };

  // Yazdıkça her tuşta istek atılmasın; süzgeç değişince sayfa başa döner.
  useEffect(() => {
    const t = setTimeout(() => {
      setAra((onceki) => {
        if (onceki !== araYazi.trim()) setSayfa(1);
        return araYazi.trim();
      });
    }, 300);
    return () => clearTimeout(t);
  }, [araYazi]);

  const liste = useQuery({
    queryKey: ["denemeler-tanim", ara, dilim, sayfa, suzgec],
    queryFn: () =>
      denemeleriSayfali({
        sayfa,
        boyut: SAYFA_BOYUTU,
        arama: ara || undefined,
        dilim: dilim === "tumu" ? undefined : dilim,
        ...suzgec,
      }),
    // Sayfa/süzgeç değişirken eski sonuç ekranda kalır: arama kutusu ve sekmeler
    // yeniden çizilmez (odak kaybolmaz).
    placeholderData: keepPreviousData,
    retry: false,
  });

  if (yeniAcik) return <YeniDeneme onKapat={() => setYeniAcik(false)} />;

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Denemeler alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (liste.data.toplam === 0 && ara === "" && dilim === "tumu" && !filtreEki && !suzgecVar) {
    return (
      <BosDurum
        ikon="deneme"
        baslik="Tanımlı deneme yok"
        aciklama="Ortak havuzda henüz deneme yok. Yönetim anahtarıyla birlikte deneme ekleyebilir."
        eylem={
          yonetebilir ? <Buton onClick={() => setYeniAcik(true)}>Yeni deneme</Buton> : undefined
        }
      />
    );
  }

  return (
    <DenemeTablosu
      denemeler={liste.data.denemeler}
      toplam={liste.data.toplam}
      sayfa={sayfa}
      sayfaBoyutu={SAYFA_BOYUTU}
      onSayfa={setSayfa}
      yukleniyor={liste.isFetching}
      araYazi={araYazi}
      onAra={setAraYazi}
      dilim={dilim}
      onDilim={(d) => {
        setDilim(d);
        setSayfa(1);
      }}
      yonetebilir={yonetebilir}
      duzenleyebilir={duzenleyebilir}
      atayabilir={atayabilir}
      acik={acik}
      onAc={(id) => setAcik(acik === id ? null : id)}
      onYeni={() => setYeniAcik(true)}
      filtreEki={
        <>
          <Alan
            etiket="Eğitim yılı filtresi"
            placeholder="2026-2027"
            value={yil}
            onChange={(e) => suzgecAlani(setYil)(e.target.value)}
          />
          <Secim
            etiket="Dönem filtresi"
            value={donem}
            onChange={(e) => suzgecAlani(setDonem)(e.target.value)}
          >
            <option value="">Tüm dönemler</option>
            <option value="1">1. dönem</option>
            <option value="2">2. dönem</option>
          </Secim>
          <Secim
            etiket="Yayınevi filtresi"
            value={yayinevi}
            onChange={(e) => suzgecAlani(setYayinevi)(e.target.value)}
          >
            <option value="">Tüm yayınevleri</option>
            {(yayinevleri.data ?? []).map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Seri adı filtresi"
            value={seri}
            onChange={(e) => suzgecAlani(setSeri)(e.target.value)}
          />
          <Buton
            disabled={!suzgecCozumu.success}
            onClick={() => {
              if (suzgecCozumu.success) {
                setSuzgec(suzgecCozumu.data);
                setSayfa(1);
              }
            }}
          >
            Kimlik filtrelerini uygula
          </Buton>
          <Buton
            gorunum="ikincil"
            disabled={!suzgecVar && !yil && !donem && !yayinevi && !seri}
            onClick={() => {
              setYil("");
              setDonem("");
              setYayinevi("");
              setSeri("");
              setSuzgec({});
              setSayfa(1);
            }}
          >
            Kimlik filtrelerini temizle
          </Buton>
          {!suzgecCozumu.success && <p role="status">{suzgecCozumu.error.issues[0]?.message}</p>}
          {filtreEki}
        </>
      }
    />
  );
}

type DenemeDilimi = "tumu" | "yaklasan" | "devam" | "gecmis";

const DILIM_SEKMELERI: ReadonlyArray<{ deger: DenemeDilimi; etiket: string }> = [
  { deger: "tumu", etiket: "Tümü" },
  { deger: "yaklasan", etiket: "Yaklaşan" },
  { deger: "devam", etiket: "Devam Eden" },
  { deger: "gecmis", etiket: "Geçmiş" },
];

/*
  Dilim (Yaklaşan / Devam eden / Geçmiş) SUNUCUDA hesaplanır: istemci yalnız yüklenen
  sayfayı süzseydi dilim başka sayfadaki denemeleri göstermezdi.
*/

/**
 * DENEME LİSTESİ — UNİKOÇ "Deneme Sınavları" kalıbı (sürüm 9a, B3; tarama-1 §3.4):
 * filtre kartı, dilim hapları, degrade başlıklı tablo. Satır AÇILIR: cevap
 * anahtarı, katılım ve PDF yönetimi UNİKOÇ'ta olmayan ama bizde şart olan
 * işler, bu yüzden "Detaylar" satırın altında açılıyor (altSatır).
 */
function DenemeTablosu({
  denemeler,
  toplam,
  sayfa,
  sayfaBoyutu,
  onSayfa,
  yukleniyor,
  araYazi,
  onAra,
  dilim,
  onDilim,
  yonetebilir,
  duzenleyebilir,
  atayabilir,
  acik,
  onAc,
  onYeni,
  filtreEki,
}: {
  denemeler: DenemeSatiri[];
  /** Süzgeçten sonraki GERÇEK toplam (sunucu); gösterilen sayfa değil. */
  toplam: number;
  sayfa: number;
  sayfaBoyutu: number;
  onSayfa: (s: number) => void;
  yukleniyor: boolean;
  araYazi: string;
  onAra: (v: string) => void;
  dilim: DenemeDilimi;
  onDilim: (d: DenemeDilimi) => void;
  yonetebilir: boolean;
  duzenleyebilir: boolean;
  atayabilir: boolean;
  acik: string | null;
  onAc: (id: string) => void;
  onYeni: () => void;
  filtreEki?: ReactNode;
}) {
  const gorunen = denemeler;
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / sayfaBoyutu));

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama="PDF'leri yönet, filtrele, öğrencilere göre listele."
        eylemler={
          yonetebilir ? (
            <Buton onClick={onYeni}>
              <Ikon ad="ekle" boyut="kucuk" /> Yeni Deneme Sınavı
            </Buton>
          ) : undefined
        }
      />
      <FiltreKarti
        baslik="Filtreler"
        ikon="filtre"
        sutun={2}
        eylemler={
          <Buton gorunum="kesikli" onClick={() => onAra("")} disabled={araYazi === ""}>
            Temizle
          </Buton>
        }
      >
        <Alan
          etiket="Metin ara (ad)"
          value={araYazi}
          onChange={(e) => onAra(e.target.value)}
          placeholder="Deneme adı…"
        />
        {filtreEki}
      </FiltreKarti>

      <Sekmeler etiket="Deneme dilimi" secili={dilim} onSec={onDilim} sekmeler={DILIM_SEKMELERI} />

      <p className="text-metin-ikincil text-sm" aria-busy={yukleniyor}>
        {toplam} deneme
      </p>

      {gorunen.length === 0 ? (
        <BosDurum baslik="Eşleşen deneme yok" aciklama="Filtreyi ya da sekmeyi değiştirin." />
      ) : (
        <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
          <Tablo
            baslikTuru="degrade"
            satirlar={gorunen}
            anahtar={(d) => d.id}
            sutunlar={[
              {
                baslik: "Ad",
                mobil: true,
                hucre: (d) => (
                  <span className="flex flex-col">
                    <span className="text-metin-koyu font-semibold">{d.ad}</span>
                    <span className="text-metin-ikincil text-xs">
                      {TUR_ADI[d.tur] ?? d.tur}
                      {d.yayinevi ? ` · ${d.yayinevi}` : ""}
                      {d.academicYear ? ` · ${d.academicYear}` : ""}
                      {d.term ? ` · ${d.term}. dönem` : ""}
                      {d.seriesName ? ` · ${d.seriesName}` : ""}
                    </span>
                  </span>
                ),
              },
              {
                baslik: "Sınav PDF'i",
                hucre: (d) =>
                  d.pdfUrl ? (
                    <a
                      href={d.pdfUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="bg-planli-zemin text-planli-metin inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                    >
                      <Ikon ad="deneme" boyut="kucuk" /> PDF Görüntüle
                    </a>
                  ) : (
                    <span className="text-metin-ikincil text-xs">PDF yok</span>
                  ),
              },
              {
                baslik: "Katılım",
                hucre: (d) => (
                  <span className="border-mor-kenar text-metin-koyu inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                    {d.katilimSayisi} öğrenci
                  </span>
                ),
              },
              {
                baslik: "Sınav Tarihi",
                hucre: (d) => <span className="tabular-nums">{yerelAnMetni(d.baslangic)}</span>,
              },
              {
                baslik: "Bitiş Tarihi",
                hucre: (d) => <span className="tabular-nums">{yerelAnMetni(d.bitis)}</span>,
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (d) => {
                  const eksik = d.bolumler.filter((b) => !b.anahtarTam).length;
                  return eksik > 0 ? (
                    <DurumRozeti durum="eksik">{eksik} bölümde anahtar eksik</DurumRozeti>
                  ) : (
                    <DurumRozeti durum="tamamlandi">Anahtar tam</DurumRozeti>
                  );
                },
              },
              {
                baslik: "İşlemler",
                mobil: true,
                etiketGizli: true,
                hucre: (d) => (
                  <Buton
                    onClick={() => onAc(d.id)}
                    aria-expanded={acik === d.id}
                    aria-label={`Detaylar — ${d.ad}`}
                  >
                    Detaylar
                  </Buton>
                ),
              },
            ]}
            altSatir={(d) =>
              acik === d.id ? (
                <div className="px-1 pb-3 md:px-3">
                  <Bolumler
                    deneme={d}
                    yonetebilir={yonetebilir}
                    duzenleyebilir={duzenleyebilir}
                    atayabilir={atayabilir}
                  />
                </div>
              ) : null
            }
          />
        </Kart>
      )}

      {sayfaSayisi > 1 && (
        <nav
          aria-label="Deneme sayfaları"
          className="flex flex-wrap items-center justify-between gap-2"
        >
          <Buton
            gorunum="ikincil"
            disabled={sayfa <= 1 || yukleniyor}
            onClick={() => onSayfa(sayfa - 1)}
          >
            Önceki
          </Buton>
          <span className="text-metin-ikincil text-sm tabular-nums">
            {sayfa}. sayfa / {sayfaSayisi}
          </span>
          <Buton
            gorunum="ikincil"
            disabled={sayfa >= sayfaSayisi || yukleniyor}
            onClick={() => onSayfa(sayfa + 1)}
          >
            Sonraki
          </Buton>
        </nav>
      )}
    </div>
  );
}

/**
 * KİTAPÇIK PDF — F-EXM-01b (A1, 05.10.2026).
 *
 * `Exam.pdfUrl` şemada vardı ve öğrenci listesi "Kitapçık PDF"
 * bağlantısını çiziyordu, ama hiçbir ekran alanı dolduramıyordu.
 * F-EXM-14 — düzenleme hem yeni izin hem sunucunun satır ipucunu ister; oluşturma izni U/D açmaz.
 */
function KitapcikPdf({
  deneme,
  duzenleyebilir,
}: {
  deneme: DenemeSatiri;
  duzenleyebilir: boolean;
}) {
  const qc = useQueryClient();
  const [duzenle, setDuzenle] = useState(false);
  const [deger, setDeger] = useState(deneme.pdfUrl ?? "");
  const hata = baglantiHatasi(deger);

  const kaydet = useMutation({
    mutationFn: (pdfUrl: string | null) => denemeGuncelle(deneme.id, { pdfUrl }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
      setDuzenle(false);
    },
  });

  if (!duzenle) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-notr-700 font-medium">Kitapçık PDF:</span>
        {deneme.pdfUrl ? (
          <a
            href={deneme.pdfUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-marka-700 underline"
          >
            Bağlantıyı aç
          </a>
        ) : (
          <span className="text-notr-600">eklenmedi</span>
        )}
        {duzenleyebilir && deneme.duzenleyebilir && (
          <Buton
            gorunum="sessiz"
            onClick={() => {
              setDeger(deneme.pdfUrl ?? "");
              setDuzenle(true);
            }}
          >
            {deneme.pdfUrl ? "PDF'i değiştir" : "PDF ekle"}
          </Buton>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Alan
        etiket="Kitapçık PDF bağlantısı"
        type="url"
        inputMode="url"
        value={deger}
        onChange={(e) => setDeger(e.target.value)}
        hata={hata ?? undefined}
        yardim="http:// ya da https:// ile başlayan bağlantı. Boş bırakıp kaydederseniz kaldırılır."
      />
      {kaydet.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={kaydet.isPending}
          disabled={hata !== null}
          onClick={() => kaydet.mutate(deger.trim() ? deger.trim() : null)}
        >
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" onClick={() => setDuzenle(false)}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

/** F-EXM-14 — ortak tanım arşivden sonra listeden kalkacağı için kullanıcı etkisini onaylar. */
function DenemeArsivle({ denemeId }: { denemeId: string }) {
  const qc = useQueryClient();
  const [onayAcik, setOnayAcik] = useState(false);
  const arsivle = useMutation({
    mutationFn: () =>
      api<{ arsivlendi: boolean }>(`/deneme/${encodeURIComponent(denemeId)}/arsivle`, {
        yontem: "POST",
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
    },
  });
  return (
    <div className="flex flex-col items-start gap-2">
      {onayAcik ? (
        <>
          <p>Deneme ortak havuzdan kaldırılacak. Sonuçlar silinmez.</p>
          <Buton yukleniyor={arsivle.isPending} onClick={() => arsivle.mutate()}>
            Arşivlemeyi onayla
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setOnayAcik(false)}>
            Vazgeç
          </Buton>
        </>
      ) : (
        <Buton gorunum="ikincil" onClick={() => setOnayAcik(true)}>
          Denemeyi arşivle
        </Buton>
      )}
      {arsivle.isError && (
        <p role="alert">
          {arsivle.error instanceof ApiHatasi ? arsivle.error.message : "Arşivlenemedi."}
        </p>
      )}
    </div>
  );
}

function Bolumler({
  deneme,
  yonetebilir,
  duzenleyebilir,
  atayabilir,
}: {
  deneme: DenemeSatiri;
  yonetebilir: boolean;
  duzenleyebilir: boolean;
  atayabilir: boolean;
}) {
  return (
    <div className="rounded-kart border-notr-200 shadow-kart ml-4 flex flex-col gap-3 border p-4">
      <p className="text-notr-600 text-xs">
        Cevap giriş penceresi: {pencereMetni(deneme.baslangic, deneme.bitis)} ·{" "}
        {deneme.katilimSayisi} katılım · {KAYNAK_ADI[deneme.kaynak] ?? deneme.kaynak}
      </p>

      <KitapcikPdf deneme={deneme} duzenleyebilir={duzenleyebilir} />

      {/* F-EXM-01b — PDF düzenlemeyle AYNI koşul; karar sunucuda. */}
      {duzenleyebilir && deneme.duzenleyebilir && (
        <DenemePencereDuzenle
          deneme={deneme}
          mevcutMetin={pencereMetni(deneme.baslangic, deneme.bitis)}
        />
      )}

      {duzenleyebilir && deneme.duzenleyebilir && <DenemeArsivle denemeId={deneme.id} />}

      <DenemeKatilim deneme={deneme} atayabilir={atayabilir} />

      {deneme.bolumler.length === 0 ? (
        <p className="text-notr-600 text-sm">
          Bu türün bölüm şablonu yok; bölümler elle tanımlanmalı.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {deneme.bolumler.map((b) => (
            <li key={b.id}>
              <BolumKarti bolum={b} yonetebilir={yonetebilir} />
            </li>
          ))}
        </ul>
      )}

      <SoruAnalizi denemeId={deneme.id} />
      <AnahtarGorunumu denemeId={deneme.id} yonetebilir={yonetebilir} />
    </div>
  );
}

function BolumKarti({ bolum, yonetebilir }: { bolum: DenemeBolumu; yonetebilir: boolean }) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [metin, setMetin] = useState("");

  const kaydet = useMutation({
    mutationFn: () => anahtarKaydet(bolum.id, metin.trim()),
    onSuccess: async () => {
      setMetin("");
      setAcik(false);
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
    },
  });

  return (
    <div className="rounded-kart bg-notr-50 flex flex-col gap-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-800 text-sm">
          {bolum.ad} · {bolum.soruSayisi} soru
        </p>
        <div className="flex items-center gap-2">
          {bolum.anahtarTam ? (
            <Rozet ton="onay">Anahtar tam</Rozet>
          ) : (
            <Rozet ton="uyari">
              {bolum.anahtarSayisi}/{bolum.soruSayisi}
            </Rozet>
          )}
          {yonetebilir && (
            <Buton gorunum="sessiz" onClick={() => setAcik((a) => !a)}>
              {acik ? "Kapat" : bolum.anahtarTam ? "Değiştir" : "Anahtar gir"}
            </Buton>
          )}
        </div>
      </div>

      {acik && (
        <div className="flex flex-col gap-2">
          <MetinAlani
            etiket="Cevap anahtarı"
            rows={4}
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            placeholder={"ABCDEABCDE…"}
            /*
              İPTAL İŞARETLERİ AÇIKÇA YAZILI. Kullanıcı iptal edilen
              soruyu nasıl işaretleyeceğini bilmiyorsa ya boş bırakır
              (anahtar kayar) ya da uydurur (istek reddedilir).
            */
            yardim={`${bolum.soruSayisi} harf bekleniyor. İptal edilen soru için: -  *  X`}
          />

          {kaydet.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {kaydet.error instanceof ApiHatasi
                ? (kaydet.error.ayrinti?.topluMetin ?? kaydet.error.message)
                : "Anahtar kaydedilemedi."}
            </p>
          )}

          {/*
            SUNUCU YARIM ANAHTAR KABUL ETMİYOR: çözülemeyen bir karakter
            varsa isteğin TAMAMI reddediliyor. Ekran bunu önceden
            söylüyor ki kullanıcı "bir kısmı kaydedildi mi" diye
            tahmin etmesin.
          */}
          <p className="text-notr-600 text-xs">
            Tanınmayan bir karakter varsa anahtarın tamamı reddedilir — yarım anahtar kaydedilmez.
          </p>

          <div>
            <Buton
              yukleniyor={kaydet.isPending}
              disabled={metin.trim().length === 0}
              onClick={() => kaydet.mutate()}
            >
              Anahtarı kaydet
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Bölüm satırlarının hatası — yoksa `null`.
 *
 * ================================================================
 * SINIR DEĞİL, KURALIN KENDİSİ SÖZLEŞMEDEN
 * ================================================================
 * İlk hâlinde sınırlar (1–120, 1–500) burada ELLE yazılıydı ve borcu
 * `TODO(Faz 6)` diye bildirmiştim: aynı sayı sunucu denetleyicisinde
 * de vardı. Şema `packages/contracts`a taşınınca borç ödendi — ama
 * sabitleri kopyalamakla değil, **şemanın kendisini kullanarak.**
 *
 * Farkı önemli: sabitleri alsaydım kural değiştiğinde (örneğin ada
 * bir biçim kısıtı eklenseydi) panel yine ayrışırdı. Şemayı
 * kullanınca panel ile sunucu AYNI kuralı çalıştırıyor ve mesajlar da
 * aynı — kullanıcı burada gördüğü cümlenin aynısını sunucudan da
 * görüyor.
 *
 * Buradaki kapı sunucuyu gereksiz kılmıyor; asıl kapı orada. Bu,
 * kullanıcının gönderip reddedilmeyi beklemek zorunda kalmaması için.
 */
function bolumHatasiniBul(satirlar: ReadonlyArray<{ ad: string; soru: string }>): string | null {
  for (const [i, b] of satirlar.entries()) {
    const sonuc = denemeBolumSemasi.safeParse({
      name: b.ad,
      // Boş alan `Number("")` ile 0'a düşüyor ve şema onu "en az bir
      // soru" diye reddediyor — boş ile sıfır aynı cevabı alıyor ama
      // ikisi de geçersiz olduğu için ayırmaya gerek yok.
      questionCount: Number(b.soru),
    });
    if (!sonuc.success) {
      return `${i + 1}. bölüm: ${sonuc.error.issues[0]?.message ?? "Geçersiz."}`;
    }
  }
  return null;
}

function YeniDeneme({ onKapat }: { onKapat: () => void }) {
  const qc = useQueryClient();
  const [ad, setAd] = useState("");
  const [tur, setTur] = useState<string>("TYT");
  const yayinevleri = useQuery({ queryKey: ["deneme-yayinevleri"], queryFn: havuzYayinevleri });
  const [yil, setYil] = useState("2026-2027");
  const [donem, setDonem] = useState("1");
  const [seri, setSeri] = useState("");
  const [anahtarlar, setAnahtarlar] = useState<Record<number, string>>({});
  const [kazanimlar, setKazanimlar] = useState<Record<number, string>>({});
  const [onIzleme, setOnIzleme] = useState<string | null>(null);
  const [yayinevi, setYayinevi] = useState("");
  const [yeniYayinevi, setYeniYayinevi] = useState("");
  const yayineviEkle = useMutation({
    mutationFn: () => havuzYayineviEkle(yeniYayinevi),
    onSuccess: async (y) => {
      await qc.invalidateQueries({ queryKey: ["deneme-yayinevleri"] });
      setYayinevi(y.id);
      setYeniYayinevi("");
    },
  });
  // F-EXM-01b — kural sunucuyla AYNI sema (`baglantiSemasi`).
  const [pdf, setPdf] = useState("");
  const pdfHatasi = baglantiHatasi(pdf);
  const [tarih, setTarih] = useState(bugunISO());
  const [baslangic, setBaslangic] = useState(bugunISO());
  const [bitis, setBitis] = useState("");
  /*
    F-EXM-01a — SAATLI PENCERE (A3). Varsayilanlar eski davranisi
    koruyor (gun basi – gun sonu); koc deneme saatini daraltabilir.
  */
  const [baslangicSaat, setBaslangicSaat] = useState("00:00");
  const [bitisSaat, setBitisSaat] = useState("23:59");
  // F-EXM-14/16 — bölüm, anahtar ve kazanım aynı havuz isteğinde doğrulanır.
  const [bolumler, setBolumler] = useState<Array<{ ad: string; soru: string }>>([]);

  const bolumHatasi = bolumHatasiniBul(bolumler);

  const olustur = useMutation({
    mutationFn: (onIzleme: boolean) => {
      let numara = 0;
      const sorular = bolumler.flatMap((_, i) =>
        (anahtarlar[i] ?? "")
          .replace(/\s/g, "")
          .toUpperCase()
          .split("")
          .filter(Boolean)
          .map((correctAnswer) => ({
            sectionId: String(i + 1),
            questionNo: ++numara,
            correctAnswer,
          })),
      );
      const satirlar = bolumler.flatMap((_, i) =>
        (kazanimlar[i] ?? "")
          .split("\n")
          .filter((s) => s.trim())
          .map((satir) => {
            const [soruNo, ...metin] = satir.split(";");
            return {
              sectionId: String(i + 1),
              questionNo: Number(soruNo),
              outcome: metin.join(";").trim(),
            };
          }),
      );
      return havuzKaydet({
        ad: ad.trim(),
        kind: tur,
        academicYear: yil,
        term: Number(donem),
        publisherId: yayinevi,
        seriesName: seri,
        ...(pdf.trim() ? { pdfUrl: pdf.trim() } : {}),
        denemeTarihi: tarih,
        baslangic: baslangicAni?.toISOString(),
        bitis: bitisAni?.toISOString(),
        bolumler: bolumler.map((b, i) => ({
          sectionId: String(i + 1),
          name: b.ad.trim(),
          questionCount: Number(b.soru),
        })),
        sorular,
        ...(satirlar.length > 0 || onIzleme ? { kazanimlar: { satirlar, onIzleme } } : {}),
      });
    },
    onSuccess: async (sonuc) => {
      if (sonuc.onIzleme) {
        setOnIzleme(
          `${sonuc.soruSayisi} soru ve ${sonuc.kazanimSayisi} kazanım doğrulandı. Henüz kaydedilmedi.`,
        );
        return;
      }
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
      onKapat();
    },
  });

  const saatGecerli = (s: string) => /^\d{2}:\d{2}$/.test(s);
  const eksik =
    ad.trim().length === 0 ||
    bitis.length === 0 ||
    tarih.length === 0 ||
    baslangic.length === 0 ||
    !saatGecerli(baslangicSaat) ||
    !saatGecerli(bitisSaat);
  const baslangicAni = eksik ? null : yerelAn(baslangic, baslangicSaat);
  const bitisAni = eksik ? null : yerelAn(bitis, bitisSaat);
  const pencereTers =
    baslangicAni !== null && bitisAni !== null && bitisAni.getTime() <= baslangicAni.getTime();
  const sure =
    baslangicAni && bitisAni && !pencereTers ? pencereSuresiMetni(baslangicAni, bitisAni) : null;

  return (
    <Kart
      baslik="Yeni deneme"
      aciklama="Ortak havuza tek kitapçık, bölüm başına cevap anahtarı ve varsa soru-kazanım listesi girin."
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Deneme adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            yardim="Öğrenci bu adı görecek."
          />
          <Secim etiket="Tür" value={tur} onChange={(e) => setTur(e.target.value)}>
            {TURLER.map((t) => (
              <option key={t} value={t}>
                {TUR_ADI[t]}
              </option>
            ))}
          </Secim>
          <Alan etiket="Eğitim yılı" value={yil} onChange={(e) => setYil(e.target.value)} />
          <Secim etiket="Dönem" value={donem} onChange={(e) => setDonem(e.target.value)}>
            <option value="1">1. dönem</option>
            <option value="2">2. dönem</option>
          </Secim>
          <Secim etiket="Yayınevi" value={yayinevi} onChange={(e) => setYayinevi(e.target.value)}>
            <option value="">Yayınevi seçin</option>
            {(yayinevleri.data ?? []).map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Secim>
          {yayinevleri.isError && <p role="alert">Yayınevleri yüklenemedi.</p>}
          {yayinevleri.data?.length === 0 && (
            <p>Henüz etkin yayınevi yok. Yönetim yayınevi listesini tamamlamalı.</p>
          )}
          <div className="flex flex-col gap-2">
            <Alan
              etiket="Yeni yayınevi adı"
              value={yeniYayinevi}
              onChange={(e) => setYeniYayinevi(e.target.value)}
            />
            <Buton
              gorunum="ikincil"
              yukleniyor={yayineviEkle.isPending}
              onClick={() => yayineviEkle.mutate()}
            >
              Yayınevi ekle
            </Buton>
            {yayineviEkle.isError && (
              <p role="alert">
                {yayineviEkle.error instanceof ApiHatasi
                  ? yayineviEkle.error.message
                  : "Yayınevi eklenemedi."}
              </p>
            )}
          </div>
          <Alan etiket="Seri / ay adı" value={seri} onChange={(e) => setSeri(e.target.value)} />
          <Alan
            etiket="Deneme tarihi"
            type="date"
            value={tarih}
            onChange={(e) => setTarih(e.target.value)}
            yardim="Denemenin çözüldüğü gün."
          />
          <Alan
            etiket="Kitapçık PDF bağlantısı"
            type="url"
            inputMode="url"
            value={pdf}
            onChange={(e) => setPdf(e.target.value)}
            hata={pdfHatasi ?? undefined}
            yardim="İsteğe bağlı. Öğrenci listesinde “Kitapçık PDF” olarak görünür."
          />
          <div className="grid grid-cols-[1fr_7.5rem] items-start gap-2">
            <Alan
              etiket="Cevap girişi başlangıcı"
              type="date"
              value={baslangic}
              onChange={(e) => setBaslangic(e.target.value)}
            />
            <Alan
              etiket="Başlangıç saati"
              type="time"
              value={baslangicSaat}
              onChange={(e) => setBaslangicSaat(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-[1fr_7.5rem] items-start gap-2">
            <Alan
              etiket="Cevap girişi bitişi"
              type="date"
              value={bitis}
              onChange={(e) => setBitis(e.target.value)}
              hata={pencereTers ? "Bitiş, başlangıçtan sonra olmalı." : undefined}
              // F-EXM-12: cevap anahtari ogrenciye bu pencere kapanana
              // kadar acilmiyor. Kullanicinin bunu bilmesi lazim, cunku
              // pencereyi cok uzun tutmak anahtari da geciktiriyor.
              yardim="Anahtar öğrenciye bu tarihten sonra açılır."
            />
            <Alan
              etiket="Bitiş saati"
              type="time"
              value={bitisSaat}
              onChange={(e) => setBitisSaat(e.target.value)}
            />
          </div>
          {sure && (
            <p className="text-notr-600 text-sm sm:col-span-2" aria-live="polite">
              Pencere süresi: {sure} (saatler Türkiye saatiyle)
            </p>
          )}
        </div>

        <div className="border-notr-200 flex flex-col gap-2 border-t pt-3">
          <p className="text-notr-800 text-sm font-medium">Bölümler</p>
          <p className="text-notr-600 text-xs">
            Bölümün soru sayısını ve anahtarını birlikte girin. Sorular deneme boyunca sıralı
            numaralanır. Kazanım satırı: soru numarası; kazanım.
          </p>

          {bolumler.map((b, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2">
              <Alan
                etiket={`${i + 1}. bölüm adı`}
                className="min-w-40 flex-1"
                value={b.ad}
                onChange={(e) =>
                  setBolumler(bolumler.map((x, j) => (j === i ? { ...x, ad: e.target.value } : x)))
                }
              />
              <Alan
                etiket="Soru sayısı"
                type="number"
                className="w-32"
                value={b.soru}
                onChange={(e) =>
                  setBolumler(
                    bolumler.map((x, j) => (j === i ? { ...x, soru: e.target.value } : x)),
                  )
                }
              />
              <MetinAlani
                etiket={`${i + 1}. bölüm cevap anahtarı`}
                value={anahtarlar[i] ?? ""}
                onChange={(e) => setAnahtarlar({ ...anahtarlar, [i]: e.target.value })}
              />
              <MetinAlani
                etiket={`${i + 1}. bölüm kazanımları`}
                value={kazanimlar[i] ?? ""}
                onChange={(e) => setKazanimlar({ ...kazanimlar, [i]: e.target.value })}
                yardim="Her satıra soru numarası; kazanım yazın. İsteğe bağlı."
              />
              <Buton
                gorunum="sessiz"
                onClick={() => {
                  setBolumler(bolumler.filter((_, j) => j !== i));
                  setAnahtarlar(
                    Object.fromEntries(
                      bolumler
                        .filter((_, j) => j !== i)
                        .map((_, j) => [j, anahtarlar[j >= i ? j + 1 : j] ?? ""]),
                    ),
                  );
                  setKazanimlar(
                    Object.fromEntries(
                      bolumler
                        .filter((_, j) => j !== i)
                        .map((_, j) => [j, kazanimlar[j >= i ? j + 1 : j] ?? ""]),
                    ),
                  );
                }}
              >
                Kaldır<span className="sr-only"> — {i + 1}. bölüm</span>
              </Buton>
            </div>
          ))}

          {bolumHatasi && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {bolumHatasi}
            </p>
          )}

          <div>
            <Buton
              gorunum="ikincil"
              onClick={() => setBolumler([...bolumler, { ad: "", soru: "" }])}
            >
              Bölüm ekle
            </Buton>
          </div>
        </div>

        {onIzleme && <p role="status">{onIzleme}</p>}
        {olustur.isError && (
          <div role="alert" className="text-hata-500 text-sm font-medium">
            <p>
              {olustur.error instanceof ApiHatasi ? olustur.error.message : "Deneme açılamadı."}
            </p>
            {olustur.error instanceof ApiHatasi && olustur.error.ayrinti && (
              <ul>
                {Object.entries(olustur.error.ayrinti).map(([sira, neden]) => (
                  <li key={sira}>{neden}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* F-EXM-16 — boş listeyi sözleşmenin reddedeceği bir ön izlemeye göndermeyiz. */}
        {!Object.values(kazanimlar).some((k) => k.trim()) && (
          <p className="text-notr-600 text-sm">Ön izleme için en az bir kazanım satırı girin.</p>
        )}
        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={olustur.isPending}
            disabled={eksik || pencereTers || bolumHatasi !== null || pdfHatasi !== null}
            onClick={() => olustur.mutate(false)}
          >
            Denemeyi tanımla
          </Buton>
          <Buton
            gorunum="ikincil"
            disabled={eksik || !Object.values(kazanimlar).some((k) => k.trim())}
            onClick={() => olustur.mutate(true)}
          >
            Kazanımları ön izle
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * SORU BAZLI ANALİZ — F-EXM-07/08
 *
 * `GET /denemeler/:id/analiz` yazılıydı, hiçbir ekran çağırmıyordu:
 * "hangi soruyu kaç öğrenci yanlış yaptı" hesaplanıyor ve kimse
 * görmüyordu. Bu, bir sonraki haftanın programını kuran uzmanın en
 * doğrudan girdisi.
 *
 * ================================================================
 * DOĞRU ORANI BOŞLARI PAYDAYA ALMIYOR
 * ================================================================
 * Sunucunun kararı ve ekran da öyle yazıyor: ölçülen şey SORUNUN
 * ZORLUĞU, öğrencinin zaman yönetimi değil. Boş bırakma ayrı bir
 * sayı olarak duruyor.
 */
function SoruAnalizi({ denemeId }: { denemeId: string }) {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["deneme-analizi", denemeId],
    queryFn: () => denemeAnalizi(denemeId),
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Soru bazlı analiz
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-notr-800 text-sm font-semibold">Soru bazlı analiz</h4>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {error instanceof ApiHatasi ? error.message : "Analiz alınamadı."}
        </p>
      ) : !data.cevapliSonuc ? (
        <p className="text-notr-600 text-sm">
          {/*
            CEVAP KODLAMASI YOKSA analiz üretilemiyor ve bu bir ARIZA
            DEĞİL: net girişiyle sonuç alınmış bir denemede soru bazlı
            veri hiç yoktur. "Analiz yok" demek eksik bir şey varmış
            izlenimi verirdi.
          */}
          Bu denemede soru bazlı kodlama yok ({data.katilimci} katılım). Soru analizi ancak
          öğrenciler cevaplarını kodladığında üretilir; net girişiyle alınan sonuçlarda hangi
          sorunun yanlış yapıldığı bilgisi bulunmaz.
        </p>
      ) : (
        <>
          <p className="text-notr-600 text-xs">
            {data.katilimci} katılımcı · boş bırakma oranı{" "}
            {data.bosBirakmaOrani === null
              ? "ölçülemedi"
              : `%${Math.round(data.bosBirakmaOrani * 100)}`}
          </p>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Soru bazlı doğru oranları</caption>
              <thead>
                <tr className="border-notr-200 text-notr-600 border-b text-left text-xs">
                  <th scope="col" className="p-2 font-medium">
                    Soru
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Çözen
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Doğru
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Oran
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Boş
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.sorular.map((q) => (
                  <tr key={q.questionNo} className="border-notr-100 border-b last:border-0">
                    <td className="p-2 tabular-nums">{q.questionNo}</td>
                    <td className="p-2 text-right tabular-nums">{q.cozenSayisi}</td>
                    <td className="p-2 text-right tabular-nums">{q.dogruSayisi}</td>
                    <td className="p-2 text-right tabular-nums">
                      {/*
                        `null` = ÇÖZEN YOK. "%0 doğru" yazmak, kimsenin
                        denemediği bir soruyu herkesin yanlış yaptığı
                        gibi gösterirdi.
                      */}
                      {q.dogruOrani === null ? (
                        <span className="text-notr-600">—</span>
                      ) : (
                        `%${Math.round(q.dogruOrani * 100)}`
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums">{q.bosSayisi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * CEVAP ANAHTARI GÖRÜNÜMÜ — F-EXM-12
 *
 * `GET /denemeler/:id/anahtar` yazılıydı, hiçbir ekran çağırmıyordu:
 * anahtar KAYDEDİLEBİLİYOR ama girildikten sonra bir daha
 * GÖRÜLEMİYORDU. Yanlış girilmiş bir cevabı fark etmenin yolu yoktu.
 *
 * Uç `@KimlikGerekli()` ama içeride kapı var: öğrenciye cevap giriş
 * penceresi kapanmadan açılmıyor. Personelde `deneme.cevapanahtari.duzenle`
 * varsa her zaman açık. Okuma `DATA_READ` olarak kayda geçtiği için
 * ancak AÇILDIĞINDA isteniyor — açılmayan bir paneli okumuş gibi
 * kaydetmek denetim kaydını kirletirdi.
 */
function AnahtarGorunumu({ denemeId, yonetebilir }: { denemeId: string; yonetebilir: boolean }) {
  const [iptalSorusu, setIptalSorusu] = useState<{
    bolumId: string;
    soruNo: number;
    islem: "soru-iptal" | "anahtar-duzeltme" | "iptal-kaldir";
    eskiCevap: string;
  } | null>(null);
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["cevap-anahtari", denemeId],
    queryFn: () => cevapAnahtari(denemeId),
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Cevap anahtarını gör
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-notr-800 text-sm font-semibold">Cevap anahtarı</h4>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {error instanceof ApiHatasi ? error.message : "Anahtar alınamadı."}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.map((b) => (
            <section key={b.id}>
              <h5 className="text-notr-800 text-sm font-medium">{b.name}</h5>
              {b.questions.length === 0 ? (
                <p className="text-notr-600 text-sm">Bu bölümde anahtar girilmemiş.</p>
              ) : (
                <ol className="mt-1 flex flex-wrap gap-1">
                  {b.questions.map((q) => (
                    <li
                      key={q.questionNo}
                      className={cn(
                        "rounded-alan px-2 py-1 text-xs tabular-nums",
                        q.isCancelled
                          ? // `notr-600`: `notr-500` bu zeminde 4,41 — AA altı.
                            // Ustu cizili bir metin zaten okumasi zor; bir de
                            // esigin altinda tutmak onu tamamen kaybettirir.
                            "bg-notr-100 text-notr-600 line-through"
                          : "bg-notr-50 text-notr-800",
                      )}
                    >
                      {q.questionNo}. {q.correctAnswer}
                      {yonetebilir && !q.isCancelled && (
                        <Buton
                          gorunum="sessiz"
                          aria-label={`İptal et: ${q.questionNo}. soru`}
                          onClick={() =>
                            setIptalSorusu({
                              bolumId: b.id,
                              soruNo: q.questionNo,
                              islem: "soru-iptal",
                              eskiCevap: q.correctAnswer,
                            })
                          }
                        >
                          İptal et
                        </Buton>
                      )}
                      {yonetebilir && (
                        <>
                          <Buton
                            gorunum="sessiz"
                            aria-label={`Anahtarı düzelt: ${q.questionNo}. soru`}
                            onClick={() =>
                              setIptalSorusu({
                                bolumId: b.id,
                                soruNo: q.questionNo,
                                islem: "anahtar-duzeltme",
                                eskiCevap: q.correctAnswer,
                              })
                            }
                          >
                            Anahtarı düzelt
                          </Buton>
                          {q.isCancelled && (
                            <Buton
                              gorunum="sessiz"
                              aria-label={`İptali kaldır: ${q.questionNo}. soru`}
                              onClick={() =>
                                setIptalSorusu({
                                  bolumId: b.id,
                                  soruNo: q.questionNo,
                                  islem: "iptal-kaldir",
                                  eskiCevap: q.correctAnswer,
                                })
                              }
                            >
                              İptali kaldır
                            </Buton>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}
          {yonetebilir && <DuzeltmeIsleri denemeId={denemeId} />}
          {iptalSorusu && (
            <SoruIptalFormu
              key={`${iptalSorusu.bolumId}:${iptalSorusu.soruNo}:${iptalSorusu.islem}`}
              denemeId={denemeId}
              {...iptalSorusu}
              kapat={() => setIptalSorusu(null)}
            />
          )}
          <p className="text-notr-600 text-xs">
            Üstü çizili sorular <strong>iptal</strong> edilmiştir; herkese doğru sayılır.
          </p>
        </div>
      )}
    </div>
  );
}

/** F-EXM-24 — kullanıcı değişecek sonuç sayısını görmeden toplu düzeltmeyi onaylayamaz. */
function SoruIptalFormu({
  denemeId,
  bolumId,
  soruNo,
  islem,
  eskiCevap,
  kapat,
}: {
  denemeId: string;
  bolumId: string;
  soruNo: number;
  islem: "soru-iptal" | "anahtar-duzeltme" | "iptal-kaldir";
  eskiCevap: string;
  kapat: () => void;
}) {
  const iptalMi = islem === "soru-iptal";
  const [yeniCevap, setYeniCevap] = useState(eskiCevap);
  const islemAdi = iptalMi
    ? "Soru iptali"
    : islem === "anahtar-duzeltme"
      ? "Anahtar düzeltmesi"
      : "İptali kaldırma";
  const [gerekce, setGerekce] = useState("");
  const [onizleme, setOnizleme] = useState<{
    surum: string;
    etkilenen: number;
    duzeltmeBekleyecek: number;
    degisen: number;
    degisenOgrenciler?: { ogrenciId: string; ad: string }[];
    zatenIptal: boolean;
    degisiklikYok?: boolean;
  } | null>(null);
  const [mesaj, setMesaj] = useState("");
  const qc = useQueryClient();
  const { hataYakala, kapi } = useSudoKapisi(
    "Anahtar düzeltmesi bütün öğrencilerin sonucunu günceller; şifrenizi yeniden doğrulayın.",
  );
  const onizle = useMutation({
    mutationFn: () =>
      api<{
        surum: string;
        etkilenen: number;
        duzeltmeBekleyecek: number;
        degisen: number;
        degisenOgrenciler?: { ogrenciId: string; ad: string }[];
        zatenIptal: boolean;
      }>(`/deneme/${encodeURIComponent(denemeId)}/${islem}/onizleme`, {
        yontem: "POST",
        govde: { bolumId, soruNo, gerekce, ...(islem === "anahtar-duzeltme" ? { yeniCevap } : {}) },
      }),
    onSuccess: (veri) => {
      setOnizleme(veri);
      setMesaj("");
    },
    onError: (hata) => {
      if (hata instanceof ApiHatasi && hata.sudoGerekliMi) hataYakala(() => onizle.mutate())(hata);
      else setMesaj(hata instanceof ApiHatasi ? hata.message : "Ön izleme alınamadı.");
    },
  });
  const onayla = useMutation({
    mutationFn: () =>
      api(`/deneme/${encodeURIComponent(denemeId)}/${islem}`, {
        yontem: "POST",
        govde: {
          bolumId,
          soruNo,
          gerekce,
          ...(islem === "anahtar-duzeltme" ? { yeniCevap } : {}),
          beklenenSurum: onizleme?.surum,
        },
      }),
    onSuccess: async () => {
      setOnizleme(null);
      setMesaj(
        iptalMi
          ? "İptal ve yeniden hesaplama kuyruğa alındı."
          : "Düzeltme ve yeniden hesaplama kuyruğa alındı.",
      );
      await qc.invalidateQueries({ queryKey: ["deneme-duzeltme-isleri", denemeId] });
      await qc.invalidateQueries({ queryKey: ["cevap-anahtari", denemeId] });
    },
    onError: (hata) => {
      if (hata instanceof ApiHatasi && hata.sudoGerekliMi) hataYakala(() => onayla.mutate())(hata);
      else {
        setOnizleme(null);
        setMesaj(hata instanceof ApiHatasi ? hata.message : "Düzeltme başlatılamadı.");
      }
    },
  });
  return (
    <section aria-label={`${soruNo}. soru: ${islemAdi}`} className="flex flex-col gap-2">
      {kapi}
      {islem === "anahtar-duzeltme" && (
        <Secim
          etiket="Yeni doğru cevap"
          value={yeniCevap}
          onChange={(e) => {
            setYeniCevap(e.target.value);
            setOnizleme(null);
            setMesaj("");
          }}
        >
          {["A", "B", "C", "D", "E"].map((harf) => (
            <option key={harf} value={harf}>
              {harf}
            </option>
          ))}
        </Secim>
      )}
      <MetinAlani
        etiket={iptalMi ? "Soru iptal gerekçesi" : "Düzeltme gerekçesi"}
        value={gerekce}
        onChange={(e) => {
          setGerekce(e.target.value);
          setOnizleme(null);
          setMesaj("");
        }}
      />
      <Buton
        disabled={gerekce.trim().length < 15 || onizle.isPending || onayla.isPending}
        onClick={() => onizle.mutate()}
      >
        {iptalMi ? "İptali ön izle" : "Düzeltmeyi ön izle"}
      </Buton>
      {onizleme && (
        <>
          <p>
            {onizleme.etkilenen} sonuç yeniden hesaplanacak; {onizleme.degisen} öğrencinin neti
            değişecek.
          </p>
          {onizleme.degisenOgrenciler && onizleme.degisenOgrenciler.length > 0 && (
            <ul aria-label="Neti değişecek öğrenciler">
              {onizleme.degisenOgrenciler.map((ogrenci) => (
                <li key={ogrenci.ogrenciId}>{ogrenci.ad}</li>
              ))}
            </ul>
          )}
          <p>{onizleme.duzeltmeBekleyecek} ders bazlı sonuç düzeltme bekleyecek.</p>
          <Buton
            disabled={
              onizleme.degisiklikYok || (iptalMi && onizleme.zatenIptal) || onayla.isPending
            }
            onClick={() => onayla.mutate()}
          >
            {iptalMi ? "Soru iptalini onayla" : "Düzeltmeyi onayla"}
          </Buton>
        </>
      )}
      {mesaj && <p role="status">{mesaj}</p>}
      <Buton gorunum="sessiz" onClick={kapat}>
        Vazgeç
      </Buton>
    </section>
  );
}

/** F-EXM-24 — atlanan PDF yönetimin düzeltme ekranında görünür; devam eden işlerin durumu yenilenir. */
function DuzeltmeIsleri({ denemeId }: { denemeId: string }) {
  const { hataYakala, kapi } = useSudoKapisi(
    "Yeniden hesap durumunu görmek için şifrenizi doğrulayın.",
  );
  const sorgu = useQuery({
    queryKey: ["deneme-duzeltme-isleri", denemeId],
    queryFn: () =>
      api<
        {
          isId: string;
          durum: string;
          asama: string | null;
          atlananRaporlar: { raporId: string; sebep: string }[];
          bildirimHatalari?: { bildirimId: string; sebep: string }[];
          hata: string | null;
        }[]
      >(`/deneme/${encodeURIComponent(denemeId)}/duzeltme-isleri`),
    retry: false,
    refetchInterval: (sorgu) => (sorgu.state.error ? false : 10000),
  });
  return (
    <section aria-label="Yeniden hesap işleri" className="flex flex-col gap-2">
      {kapi}
      <h5>Yeniden hesap işleri</h5>
      {sorgu.isError && <p role="alert">Yeniden hesap durumu alınamadı.</p>}
      <Buton
        gorunum="sessiz"
        onClick={() => {
          if (sorgu.error instanceof ApiHatasi && sorgu.error.sudoGerekliMi)
            hataYakala(() => {
              void sorgu.refetch();
            })(sorgu.error);
          else void sorgu.refetch();
        }}
      >
        Yeniden hesap durumunu yenile
      </Buton>
      {sorgu.data?.map((is) => (
        <div key={is.isId}>
          <p>
            {is.durum === "FAILED"
              ? "Düzeltme işi durdu"
              : is.asama === "TAMAM"
                ? "Düzeltme tamamlandı"
                : "Düzeltme sürüyor"}
          </p>
          {is.atlananRaporlar.map((rapor) => (
            <p role="alert" key={rapor.raporId}>
              PDF yenilenemedi: {rapor.raporId} —{" "}
              {rapor.sebep === "PDF_DENEME_SINIRI"
                ? "Yeniden deneme sınırına ulaşıldı"
                : "Rapor atlandı"}
            </p>
          ))}
          {/* F-EXM-24 — hesap tamamlandıktan sonra ulaşmayan bildirim sessizce kaybolmasın; yönetim elle haber verebilsin. */}
          {(is.bildirimHatalari ?? []).some(
            (hata) => hata.sebep === "BILDIRIM_LISTESI_HAZIRLANAMADI",
          ) && (
            <p role="alert">
              Bildirim listesi hazırlanamadı — öğrenciye ya da veliye ayrıca haber verin.
            </p>
          )}
          {(is.bildirimHatalari ?? []).filter(
            (hata) => hata.sebep !== "BILDIRIM_LISTESI_HAZIRLANAMADI",
          ).length > 0 && (
            <p role="alert">
              {
                (is.bildirimHatalari ?? []).filter(
                  (hata) => hata.sebep !== "BILDIRIM_LISTESI_HAZIRLANAMADI",
                ).length
              }{" "}
              kişiye bildirim gönderilemedi — öğrenciye ya da veliye ayrıca haber verin.
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
